'use client';

import { AnimatePresence, motion } from 'motion/react';
import { useActivitySearchMode, useAiSearchEnabled } from './useActivitySearchMode';
import { SearchModeTabs } from './SearchModeTabs';
import { AiSearchNudge } from './AiSearchNudge';
import type { ActivitySearchMode } from './shared';

/**
 * The Filters / AI segmented control. Used in the hero and in the sticky sidebar so the Base UI
 * indicator can move between the same tab layouts instead of remounting a separate control.
 */
export const ActivitySearchModeTabBar: React.FC<{
    className?: string;
    variant: 'hero' | 'sidebar';
}> = ({ className, variant = 'hero' }) => {
    const { mode, setMode } = useActivitySearchMode();
    const aiEnabled = useAiSearchEnabled();
    /** The sidebar variant always uses the light theme regardless of caller intent. */
    const theme = variant === 'sidebar' ? 'light' : 'glass';

    const handleValueChange = (next: ActivitySearchMode) => {
        setMode(next);
        /**
         * Switching to AI focuses the always-open sidebar composer so the user can type
         * immediately (FUN-4558 MVP2 feedback). Desktop only — auto-focusing on mobile would
         * pop the on-screen keyboard and shove the page around (see the keyboard feedback).
         */
        if (
            next === 'ai' &&
            typeof window !== 'undefined' &&
            window.matchMedia('(min-width: 1024px)').matches
        ) {
            requestAnimationFrame(() => {
                document
                    .querySelector<HTMLTextAreaElement>('[data-testid="ai-chat-input"]')
                    ?.focus({ preventScroll: true });
            });
        }
        /**
         * Closing the AI chat from the hero strip (i.e. dismissing the AI session entirely)
         * should restore the user to the top so the hero/filters are visible again. The
         * sidebar tab bar stays sticky and intentionally does NOT scroll.
         */
        if (variant === 'hero' && mode === 'ai' && next !== 'ai' && typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // AI Search is hidden on portals where it isn't supported yet (BIZZ).
    if (!aiEnabled) return null;

    return (
        <div className="relative flex w-full justify-center">
            {/* Nudge the user toward AI search while they're still in Filters mode — same playful
                annotation as the homepage, only on the hero tab strip (not the sticky sidebar). */}
            <AnimatePresence>
                {variant === 'hero' && mode === 'normal' && (
                    <motion.div
                        key="ai-search-nudge"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}
                        className="absolute left-[calc(50%+6rem)] top-[-2rem] z-10"
                    >
                        <AiSearchNudge className="relative left-0 top-0" />
                    </motion.div>
                )}
            </AnimatePresence>
            <SearchModeTabs
                mode={mode}
                onChange={handleValueChange}
                theme={theme}
                width={variant === 'sidebar' ? 'full' : 'hero'}
                className={className}
            />
        </div>
    );
};
